import { useTheme } from '@emotion/react';
import type { LabeledVariants } from '@hover/blueprint';
import {
  Center,
  Icon,
  cssVar,
  forwardRef,
  mapResponsive,
  motion,
  system,
} from '@hover/blueprint';
import { iconShop } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect } from 'react';

import type { ButtonProps } from 'src/shared/components';
import { Button } from 'src/shared/components';
import { fadePresence } from 'src/shared/utils/motion';

import { warn } from '../../helpers/logging';
import { useHomeownerTranslate } from '../../hooks';
import { usePartner, usePartnerMaterialCallout } from '../../hooks/usePartner';
import { AnimatedEllipsis } from '../AnimatedEllipsis';

export interface MaterialCalloutButtonProps
  extends Omit<ButtonProps, 'children' | 'iconBefore' | 'iconAfter'> {
  /**
   * The external manufacturer callout URL the button will navigate
   */
  calloutUrl: string;
  children?: never;
}

const MotionAnimatedEllipsis = motion(AnimatedEllipsis);
const MotionCenter = motion(Center);

const $collapsedWidth = cssVar('callout-button-collapsed-width');

type Variants = LabeledVariants<'loading' | 'idle'>;

const sizeVariants: Variants = {
  loading: { width: $collapsedWidth.reference },
  idle: { width: 'auto', transition: { delay: 0.15 } },
};

const labelVariants: Variants = {
  loading: { opacity: 0 },
  idle: { opacity: 1, transition: { delay: 0.25 } },
};

export const MaterialCalloutButton = forwardRef<
  MaterialCalloutButtonProps,
  'a'
>(({ calloutUrl, isLoading: isLoadingProp, size = 'large', ...props }, ref) => {
  const t = useHomeownerTranslate();

  const { isLoading: isLoadingPartner } = usePartner();
  const display = usePartnerMaterialCallout();

  const isLoading = isLoadingProp || isLoadingPartner;

  const displayLabel = display?.label;
  const fallbackLabel = t('materialCallouts.calloutButtonFallback');

  const label = displayLabel ?? fallbackLabel;

  useEffect(() => {
    if (!isLoadingPartner && !displayLabel) {
      warn('MaterialCalloutButton: failed to display partner name');
    }
  }, [displayLabel, isLoadingPartner]);

  const { sizes } = useTheme();

  const animate = {
    animate: isLoading ? 'loading' : 'idle',
    initial: 'loading',
  } as const;

  // TODO(MEOW-5879): once we've implemented this new loading state in the design system,
  // remove the ARIA attributes and `pointerEvents` in favor of the system `isLoading` state
  return (
    <Button
      aria-busy={isLoading}
      aria-disabled={isLoading}
      as="a"
      colorMode="dark"
      href={calloutUrl}
      label={label}
      pointerEvents={isLoading ? 'none' : 'auto'}
      px={0}
      size={size}
      target="_blank"
      variant="secondary"
      {...props}
      ref={ref}
    >
      <MotionCenter
        {...animate}
        height="100%"
        position="relative"
        sx={{
          [$collapsedWidth.variable]: mapResponsive(
            s => (s === 'large' ? sizes[500] : sizes[400]),
            size,
          ),
        }}
        variants={sizeVariants}
      >
        <AnimatePresence>
          {isLoading && (
            <MotionAnimatedEllipsis
              color="inherit"
              height="25"
              label={t('materialCallouts.calloutButtonLoading')}
              position="absolute"
              {...fadePresence}
            />
          )}
        </AnimatePresence>
        <motion.span
          {...animate}
          alignItems="center"
          display="inline-flex"
          mx={mapResponsive(s => (s === 'large' ? '600' : '500'), size)}
          variants={labelVariants}
        >
          <system.span>{label}</system.span>
          <Icon icon={display?.icon ?? iconShop} isInline ml="200" />
        </motion.span>
      </MotionCenter>
    </Button>
  );
});
